import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Typography,
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tooltip,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import UserPicker, { GraphUser } from "./UserPicker";
import { AppRole } from "@microsoft/microsoft-graph-types";
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import GroupIcon from '@mui/icons-material/Group';

const DEFAULT_ACCESS_ROLE_ID = "00000000-0000-0000-0000-000000000000";

/** Extended details about a user assigned to the app. */
interface ProvisionedUser {
  id: string;
  assignmentId: string;
  resourceId: string;
  principalId: string;
  displayName?: string;
  userPrincipalName?: string;
  assignedRoleDisplayName?: string;
  assignedRoleValue?: string;
}

/** Shape of each row in the bulk import file. */
interface BulkImportEntry {
  upn: string;
  roleId?: string;
}

/** Props for the UserProvisioning component. */
interface UserProvisioningProps {
  /** The Azure AD Application (client) ID. */
  appId: string;
  /** The optional servicePrincipalId if known. Otherwise, the code looks it up. */
  servicePrincipalId?: string;
  /** An optional callback for applying changes. */
  onApplyChanges?: () => void;
}

/**
 * The main provisioning component that handles:
 *  1) Displaying current users assigned to the app.
 *  2) Assigning new users with a selected role.
 *  3) Deprovisioning assigned users.
 *  4) Bulk import from JSON file.
 */
const UserProvisioning: React.FC<UserProvisioningProps> = ({
  appId,
  servicePrincipalId,
  onApplyChanges,
}) => {
  const { instance, accounts } = useMsal();

  // Form fields
  const [provisionedUserUPN, setProvisionedUserUPN] = useState("");
  const [selectedAppRole, setSelectedAppRole] = useState<string>("");

  // State for user assignments
  const [provisionedUsers, setProvisionedUsers] = useState<ProvisionedUser[]>([]);

  // Controls whether the user picker modal is open
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [graphTokenForUserSearch, setGraphTokenForUserSearch] = useState("");

  // The roles available in this SP
  const [appRoles, setAppRoles] = useState<AppRole[]>([]);

  // For Bulk Import
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // NEW! State for controlling the help dialog
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // New state for alerts
  const [showAssignmentReminder, setShowAssignmentReminder] = useState(false);
  const [showDeprovisionReminder, setShowDeprovisionReminder] = useState(false);
  const [removedUserName, setRemovedUserName] = useState("");

  // New state for editing user role assignments
  const [editingUser, setEditingUser] = useState<ProvisionedUser | null>(null);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editRoleId, setEditRoleId] = useState<string>("");
  const [showRoleUpdateReminder, setShowRoleUpdateReminder] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  /*******************************
   *       EVENT HANDLERS
   *******************************/
  /** Opens the user picker modal, after fetching a Graph token. */
  async function handleOpenUserPicker() {
    alert("After picking the user, please click 'Add User'!");
    try {
      const token = await getGraphAccessToken();
      setGraphTokenForUserSearch(token);
      setUserPickerOpen(true);
    } catch (err) {
      console.error("Failed to get Graph token for user search:", err);
      alert("Failed to get Graph token. See console for details.");
    }
  }

  /** Receives the user(s) picked from the directory and sets the UPN in the text field. */
  function handleUserPickerSubmit(selectedUsers: GraphUser[]) {
    setUserPickerOpen(false);
    if (selectedUsers.length === 0) return;
    setProvisionedUserUPN(selectedUsers[0].userPrincipalName || "");
  }

  // Update handler with alert notice
  async function handleProvisionUser() {
    if (!provisionedUserUPN) {
      alert("Please enter UPN first.");
      return;
    }
    // When roles exist, keep requiring a selection; when none exist, allow Default Access
    if (appRoles.length > 0 && !selectedAppRole) {
      alert("Please select an App Role first.");
      return;
    }

    try {
      const token = await getGraphAccessToken();
      const roleToAssign = appRoles.length === 0 ? DEFAULT_ACCESS_ROLE_ID : selectedAppRole;
      await provisionUser(token, provisionedUserUPN, roleToAssign!);

      setShowAssignmentReminder(true);
      setTimeout(() => setShowAssignmentReminder(false), 6000);

      setProvisionedUserUPN("");
      fetchProvisionedUsers();
    } catch (error) {
      console.error("Error provisioning user:", error);
      alert(`Error provisioning user: ${String(error)}`);
    }
  }

  // Update deprovision handler
  async function handleDeprovisionUser(assignmentId: string, spId: string, userName: string) {
    try {
      const token = await getGraphAccessToken();
      await deprovisionUser(token, assignmentId, spId);
      
      // Set removed user and show alert
      setRemovedUserName(userName);
      setShowDeprovisionReminder(true);
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setShowDeprovisionReminder(false);
      }, 6000);
      
      fetchProvisionedUsers();
    } catch (error) {
      console.error("Error deprovisioning user:", error);
      alert("Failed to de-provision user. See console for details.");
    }
  }

  /** Opens the role editing dialog for a specific user */
const handleEditRole = (user: ProvisionedUser) => {
  setEditingUser(user);
  // Pre-select the current role if it exists
  const currentRoleId = appRoles.find(role => role.value === user.assignedRoleValue)?.id || "";
  setEditRoleId(currentRoleId);
  setEditRoleDialogOpen(true);
  setUserMenuAnchorEl(null); // Close the menu if it was open
};

/** Handles the menu opening for a user */
const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, userId: string) => {
  setUserMenuAnchorEl(event.currentTarget);
  setSelectedUserId(userId);
};

/** Handles the menu closing */
const handleUserMenuClose = () => {
  setUserMenuAnchorEl(null);
  setSelectedUserId(null);
};

/** Updates the role assignment for a user */
const handleUpdateUserRole = async () => {
  if (!editingUser || !editRoleId) {
    alert("No user or role selected for update");
    return;
  }
  
  try {
    const token = await getGraphAccessToken();
    await updateUserRole(token, editingUser, editRoleId);
    
    // Show success message - using the same pattern as removal
    setShowRoleUpdateReminder(true);
    
    setTimeout(() => {
      setShowRoleUpdateReminder(false);
    }, 6000);
    
    // Close dialog and refresh user list
    setEditRoleDialogOpen(false);
    setEditingUser(null);
    fetchProvisionedUsers();
  } catch (error) {
    console.error("Error updating user role:", error);
    alert(`Error updating user role: ${String(error)}`);
  }
};

  /*******************************
   *       BULK IMPORT
   *******************************/
  function handleBulkImportClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; 
      fileInputRef.current.click();
    }
  }

  async function handleBulkImportFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    try {
      const text = await file.text();
      const bulkEntries: BulkImportEntry[] = JSON.parse(text);

      if (!Array.isArray(bulkEntries)) {
        throw new Error("Imported file must be a JSON array of { upn, roleId } objects.");
      }

      const token = await getGraphAccessToken();
      for (const entry of bulkEntries) {
        if (!entry.upn) {
          console.warn("Skipping invalid entry (missing upn):", entry);
          continue;
        }

        // If the app has app roles, a valid roleId is required; otherwise assign Default Access
        if (appRoles.length > 0) {
          if (!entry.roleId) {
            console.warn("Skipping entry (roleId required when app has roles):", entry);
            continue;
          }
          const roleExists = appRoles.some((r) => r.id === entry.roleId);
          if (!roleExists) {
            console.warn("Skipping entry: roleId not found in app roles =>", entry);
            continue;
          }
          try {
            await provisionUser(token, entry.upn, entry.roleId);
            console.log(`Assigned ${entry.upn} => roleId: ${entry.roleId}`);
          } catch (provisionErr) {
            console.warn("Skipping entry due to error provisioning user:", provisionErr);
          }
        } else {
          // No app roles defined => Default Access
          try {
            await provisionUser(token, entry.upn, DEFAULT_ACCESS_ROLE_ID);
            console.log(`Assigned ${entry.upn} => Default Access`);
          } catch (provisionErr) {
            console.warn("Skipping entry due to error provisioning user (Default Access):", provisionErr);
          }
        }
      }

      alert("Bulk provisioning completed! Refreshing assigned users...");
      fetchProvisionedUsers();
    } catch (err) {
      console.error("Bulk import failed:", err);
      alert(`Bulk import failed. See console.\n\n${err}`);
    }
  }

  // NEW! Show a simple help dialog
  function handleShowImportHelp() {
    setHelpDialogOpen(true);
  }
  
  function handleCloseImportHelp() {
    setHelpDialogOpen(false);
  }

  // Example of dynamic generation of a sample JSON
  function handleDownloadSampleJson() {
    // If there are roles, include roleId in the sample; otherwise only UPN (Default Access)
    let sampleContent: any[] = [];
    if (appRoles.length > 0) {
      const sampleRoleId = appRoles[0].id!;
      sampleContent = [
        { upn: "alice@contoso.com", roleId: sampleRoleId },
        { upn: "bob@contoso.com", roleId: sampleRoleId }
      ];
    } else {
      sampleContent = [
        { upn: "alice@contoso.com" },
        { upn: "bob@contoso.com" }
      ];
    }

    const blob = new Blob([JSON.stringify(sampleContent, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "sample-user-import.json";
    link.click();

    URL.revokeObjectURL(url);
  }  

  /**
   * Updates a user's role assignment by removing the existing one and creating a new one
  */
  async function updateUserRole(accessToken: string, user: ProvisionedUser, newRoleId: string) {
    const client = Client.init({ authProvider: (done) => done(null, accessToken) });
    
    // 1) Delete the old assignment
    await client.api(`/servicePrincipals/${user.resourceId}/appRoleAssignedTo/${user.assignmentId}`).delete();
    
    // 2) Create the new assignment
    const body = {
      principalId: user.principalId,
      resourceId: user.resourceId,
      appRoleId: newRoleId
    };
    
    await client.api(`/servicePrincipals/${user.resourceId}/appRoleAssignedTo`).post(body);
  }

  /*******************************
   *       MS GRAPH HELPERS
   *******************************/
  /**
   * Gets a Graph access token with the required scopes.
   */
  async function getGraphAccessToken(): Promise<string> {
    const accessTokenRequest = {
      scopes: [
        "Directory.Read.All",
        "User.ReadWrite.All",
        "Application.ReadWrite.All"
      ],
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(accessTokenRequest);
      return response.accessToken;
    } catch (err: any) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(accessTokenRequest);
        return response.accessToken;
      }
      throw err;
    }
  }

  /**
   * Provisions a user by creating an appRoleAssignment with the chosen role.
   */
  async function provisionUser(accessToken: string, upn: string, appRoleId: string) {
    const client = Client.init({ authProvider: (done) => done(null, accessToken) });

    // 1) Find the user
    const userResponse = await client
      .api(`/users?$filter=userPrincipalName eq '${upn}'`)
      .get();

    if (!userResponse.value || userResponse.value.length === 0) {
      throw new Error(`User with UPN '${upn}' not found in this tenant.`);
    }
    const userId = userResponse.value[0].id;

    // 2) Get the servicePrincipal ID
    const spId = servicePrincipalId || (await getServicePrincipalId(client));
    if (!spId) {
      throw new Error("Service principal not found for this application.");
    }

    // 3) Assign the role
    const body = {
      principalId: userId,
      resourceId: spId,
      appRoleId: appRoleId
    };
    await client.api(`/servicePrincipals/${spId}/appRoleAssignedTo`).post(body);
  }

  /**
   * Deprovisions a user by removing the role assignment from them.
   */
  async function deprovisionUser(accessToken: string, assignmentId: string, spId: string) {
    const client = Client.init({ authProvider: (done) => done(null, accessToken) });
    await client.api(`/servicePrincipals/${spId}/appRoleAssignedTo/${assignmentId}`).delete();
  }

  /**
   * Fetch all assigned users for this SP, plus the roles so we can display them.
   */
  async function fetchProvisionedUsers() {
    try {
      const token = await getGraphAccessToken();
      await fetchUsers(token);
    } catch (error) {
      console.error("Failed to fetch provisioned users:", error);
      alert("Failed to fetch provisioned users. See console for details.");
    }
  }

  /**
   * Actually calls Graph for assigned users and role data.
   */
  async function fetchUsers(accessToken: string) {
    const client = Client.init({ authProvider: (done) => done(null, accessToken) });

    const spId = servicePrincipalId || (await getServicePrincipalId(client));
    if (!spId) {
      alert("Service principal not found for the application.");
      return;
    }

    // 1) Get appRoleAssignedTo
    let allAssignments: any[] = [];
    let resp = await client.api(`/servicePrincipals/${spId}/appRoleAssignedTo`).get();
    while (resp) {
      allAssignments = [...allAssignments, ...resp.value];
      if (resp["@odata.nextLink"]) {
        resp = await client.api(resp["@odata.nextLink"]).get();
      } else {
        break;
      }
    }

    // 2) Get roles from the SP so we can map IDs to role names
    const spObj = await client.api(`/servicePrincipals/${spId}`).get();
    const spRoles: AppRole[] = spObj.appRoles || [];
    const rolesMap: Record<string, AppRole> = {};
    spRoles.forEach((role) => {
      if (role.id) {
        rolesMap[role.id] = role;
      }
    });

    // 3) For each assignment that is for a user, fetch user details and role
    const allUsers: ProvisionedUser[] = [];
    for (const assignment of allAssignments) {
      if (assignment.principalType === "User") {
        const userRes = await client.api(`/users/${assignment.principalId}`).get();
        const roleInfo = rolesMap[assignment.appRoleId] || {};
        allUsers.push({
          id: userRes.id,
          assignmentId: assignment.id,
          resourceId: assignment.resourceId,
          principalId: userRes.id,
          displayName: userRes.displayName,
          userPrincipalName: userRes.userPrincipalName,
          assignedRoleDisplayName: roleInfo.displayName || undefined,
          assignedRoleValue: roleInfo.value || undefined
        });
      }
    }
    setProvisionedUsers(allUsers);
  }

  /**
   * Looks up the servicePrincipal ID for the given appId.
   */
  async function getServicePrincipalId(client: Client): Promise<string | null> {
    const spRes = await client.api(`/servicePrincipals?$filter=appId eq '${appId}'`).get();
    if (spRes.value && spRes.value.length > 0) {
      return spRes.value[0].id;
    }
    return null;
  }

  /*******************************
   *        useEffects
   *******************************/
  useEffect(() => {
    if (appId) {
      fetchProvisionedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    async function fetchAppRoles() {
      const token = await getGraphAccessToken();
      const client = Client.init({ authProvider: (done) => done(null, token) });

      const spId = servicePrincipalId || (await getServicePrincipalId(client));
      if (!spId) {
        console.error("Service Principal not found.");
        return;
      }

      try {
        const spObj = await client.api(`/servicePrincipals/${spId}`).get();
        const roles = spObj.appRoles?.filter((role: AppRole) => role.isEnabled && 
        role.allowedMemberTypes?.includes("User")
      ) || [];
        setAppRoles(roles);
      } catch (error) {
        console.error("Error fetching app roles:", error);
      }
    }

    if (servicePrincipalId || appId) {
      fetchAppRoles();
    }
  }, [servicePrincipalId, appId, accounts, instance]);

  /*******************************
   *         RENDER UI
   *******************************/
return (
  <Card variant="outlined" sx={{ mb: 4 }}>
    <CardContent>
      {/* Main Title for the entire User Provisioning section */}
      {/* <Typography 
        variant="h6" 
        sx={{ 
          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
          fontSize: "1.3rem",
          fontWeight: 600,
          color: "primary.main",
          letterSpacing: "0.01em",
          mb: 3
        }}
      >
        User Provisioning
      </Typography> */}

      {/* 1) PROVISIONED USERS - Updated with green theme like OptionalClaims */}
      <Accordion 
        defaultExpanded
        sx={{
          border: '1px solid #e8f5e9', // Green border like OptionalClaims
          borderRadius: '12px !important', // Rounded corners like OptionalClaims
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', // Same shadow as OptionalClaims
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', // Green gradient like OptionalClaims
            borderBottom: '1px solid #c8e6c9',
            minHeight: '56px' // Same height as OptionalClaims
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28, // Same size as OptionalClaims
              height: 28,
              borderRadius: '8px', // Rounded square like OptionalClaims
              backgroundColor: '#4caf50', // Green background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <GroupIcon sx={{ color: 'white', fontSize: '0.85rem' }} /> {/* Use GroupIcon for users */}
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.2rem", // Same as OptionalClaims
                fontWeight: 600,
                color: "#2e7d32", // Green text color
                letterSpacing: "0.01em"
              }}
            >
              Provisioned Users
            </Typography>
            {/* Add count badge like OptionalClaims */}
            <Chip 
              size="small"
              label={`${provisionedUsers.length} users`}
              sx={{ 
                ml: 2,
                backgroundColor: '#c8e6c9', // Light green background
                color: '#2e7d32', // Dark green text
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ 
          backgroundColor: "#fafffe", // Light green background like OptionalClaims
          p: 3, 
          borderRadius: 1 
        }}>
          {/* Deprovision Reminder Alert */}
          {showDeprovisionReminder && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2,
              backgroundColor: '#fdf5f5', 
              borderLeft: '4px solid #f44336',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <Box sx={{ mr: 1.5, color: '#d32f2f' }}>
                <InfoOutlinedIcon />
              </Box>
              <Typography variant="body2">
                <strong>"{removedUserName}" removed!</strong> Remember to click <strong>Apply Changes</strong> to finalize this removal.
              </Typography>
            </Box>
          )}

          {showRoleUpdateReminder && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2,
              backgroundColor: '#e8f5e9', 
              borderLeft: '4px solid #4caf50',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <Box sx={{ mr: 1.5, color: '#2e7d32' }}>
                <InfoOutlinedIcon />
              </Box>
              <Typography variant="body2">
                <strong>Role updated!</strong> Remember to click <strong>Apply Changes</strong> to finalize this change.
              </Typography>
            </Box>
          )}
          
          {provisionedUsers.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              backgroundColor: '#f8f9fa', 
              borderRadius: '12px', 
              textAlign: 'center',
              border: '2px dashed #dee2e6'
            }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <GroupIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No users have been provisioned to this application
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the "Add New User" section below to provision users
              </Typography>
            </Box>
          ) : (
            <List sx={{ 
              bgcolor: "#ffffff", 
              border: "1px solid #c8e6c9", // Green border to match theme
              borderRadius: '8px', // Updated border radius
              mb: 2,
              overflow: 'hidden'
            }}>
              {provisionedUsers.map((user, index) => (
                <ListItem
                  key={user.assignmentId}
                  sx={{
                    borderBottom: index < provisionedUsers.length - 1 ? "1px solid #f5f5f5" : "none",
                    '&:hover': { backgroundColor: '#fafafa' } // Added hover effect
                  }}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleUserMenuOpen(e, user.id)}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#e8f5e9' // Green hover effect
                          }
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={userMenuAnchorEl}
                        open={Boolean(userMenuAnchorEl) && selectedUserId === user.id}
                        onClose={handleUserMenuClose}
                      >
                        <MenuItem 
                          onClick={() => handleEditRole(user)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon fontSize="small" sx={{ mr: 1 }} />
                          Edit Role
                        </MenuItem>
                        <MenuItem 
                          onClick={() => {
                            handleDeprovisionUser(
                              user.assignmentId, 
                              user.resourceId, 
                              user.displayName || user.userPrincipalName || "User"
                            );
                            handleUserMenuClose();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                          Remove
                        </MenuItem>
                      </Menu>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 1, color: '#4caf50' }} /> {/* Green icon */}
                        <Typography variant="body2" fontWeight="medium">
                          {user.displayName || user.userPrincipalName}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ mb: 0.5 }}>
                          {user.userPrincipalName}
                        </Typography>
                        {user.assignedRoleValue && (
                          <Chip 
                            label={user.assignedRoleValue} 
                            size="small"
                            sx={{
                              height: 20, 
                              fontSize: '0.7rem', 
                              maxWidth: 'fit-content',
                              backgroundColor: '#c8e6c9', // Green chip background
                              color: '#2e7d32' // Dark green text
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          
          {/* Apply Changes Button */}
          {(showDeprovisionReminder || showRoleUpdateReminder) && onApplyChanges && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 2, 
              pt: 1, 
              borderTop: '1px solid #c8e6c9' // Green border
            }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={() => {
                  onApplyChanges();
                  setShowDeprovisionReminder(false);
                  setShowRoleUpdateReminder(false);
                }}
                sx={{ borderRadius: '8px' }} // Rounded button
              >
                Apply Changes
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* 2) ADD NEW USER - Updated with blue theme like OptionalClaims */}
      <Accordion
        sx={{
          border: '1px solid #e3f2fd', // Blue border like OptionalClaims
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)', // Blue gradient like OptionalClaims
            borderBottom: '1px solid #bbdefb',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#2196f3', // Blue background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <PersonAddIcon sx={{ color: 'white', fontSize: '1rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.2rem", // Same as OptionalClaims
                fontWeight: 600,
                color: "#1565c0", // Blue text color
                letterSpacing: "0.01em"
              }}
            >
              Add New User
            </Typography>
            <Chip 
              size="small"
              label="Configure" 
              sx={{ 
                ml: 2,
                backgroundColor: '#bbdefb', // Light blue background
                color: '#1565c0', // Dark blue text
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ 
          backgroundColor: "#f8fbff", // Light blue background like OptionalClaims
          p: 3, 
          borderRadius: 1 
        }}>
          {/* Assignment Reminder Alert */}
          {showAssignmentReminder && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2,
              backgroundColor: '#fff3e0', 
              borderLeft: '4px solid #ff9800',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <Box sx={{ mr: 1.5, color: '#f57c00' }}>
                <InfoOutlinedIcon />
              </Box>
              <Typography variant="body2">
                <strong>User assigned!</strong> Remember to click <strong>Apply Changes</strong> to save your modifications.
              </Typography>
            </Box>
          )}
          
          <Stack spacing={3}>
            {/* Enhanced form with better visual grouping */}
            <Box sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e3f2fd' // Blue border
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1565c0' }}>
                User Configuration
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TextField
                    label="User Principal Name (UPN)"
                    value={provisionedUserUPN}
                    onChange={(e) => setProvisionedUserUPN(e.target.value)}
                    placeholder="user@domain.com"
                    fullWidth
                    size="small"
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px'
                      }
                    }}
                  />
                  
                  <Button 
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleOpenUserPicker}
                    sx={{ 
                      mt: 0,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Browse
                  </Button>
                </Box>

                {appRoles.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Role</InputLabel>
                    <Select
                      value={selectedAppRole}
                      label="Select Role"
                      onChange={(e) => setSelectedAppRole(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    >
                      {appRoles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Typography>{role.displayName}</Typography>
                            <Typography variant="caption" color="text.secondary">({role.value})</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Box sx={{ p: 1, color: 'text.secondary' }}>
                    <Typography variant="body2">
                      This application has no app roles. Assigning a user will grant Default Access (same as Entra portal).
                      Ensure “User assignment required” is set appropriately on the enterprise application.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Action buttons with enhanced styling */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 2, 
              borderTop: '1px solid #e3f2fd' // Blue border
            }}>
            <Button 
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleProvisionUser}
              disabled={!provisionedUserUPN || (appRoles.length > 0 && !selectedAppRole)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
            >
              Add User
            </Button>
              
              {onApplyChanges && (
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    onApplyChanges();
                    setShowAssignmentReminder(false);
                    setShowDeprovisionReminder(false);
                  }}
                  sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Apply Changes
                </Button>
              )}
            </Box>
            
            {/* Bulk operations section with enhanced styling */}
            <Box sx={{ 
              mt: 3, 
              pt: 2, 
              borderTop: '1px solid #e3f2fd', // Blue border
              p: 2,
              backgroundColor: '#f0f8ff', // Very light blue background
              borderRadius: '8px',
              border: '1px solid #e3f2fd'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle2" fontWeight="medium" sx={{ mr: 2, color: '#1565c0' }}>
                    Bulk Operations:
                  </Typography>
                  
                  <Button 
                    variant="outlined" 
                    color="warning" 
                    startIcon={<UploadFileIcon />}
                    onClick={handleBulkImportClick}
                    size="small"
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Import Users from JSON
                  </Button>
                </Box>
                
                <Tooltip title="View sample format">
                  <IconButton 
                    color="primary"
                    onClick={handleShowImportHelp}
                    size="small"
                    sx={{
                      '&:hover': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Stack>
          
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".json"
            onChange={handleBulkImportFileChange}
          />
        </AccordionDetails>
      </Accordion>
        
        {/* ABOUT USER PROVISIONING */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography 
              variant="subtitle1"
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.1rem", color: 'info.main' }} />
              About User Provisioning
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                User provisioning allows you to assign users to your application with specific roles that determine their permissions.
              </Typography>
              
              {/* Visual explanation */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#ffffff', 
                border: '1px solid #e0e0ff', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                  How Provisioning Works:
                </Typography>
                <Typography variant="body2">
                  • <strong>User Assignment</strong>: Users must be explicitly assigned to enterprise applications<br />
                  • <strong>App Roles</strong>: Roles determine what permissions a user has within the application<br />
                  • <strong>Role Claims</strong>: When a user signs in, their assigned roles are included in their access token<br />
                  • <strong>Bulk Import</strong>: For adding multiple users at once using a JSON file
                </Typography>
              </Box>
              
              <Box sx={{
                p: 2,
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                  Important Notes:
                </Typography>
                <Typography variant="body2">
                  • Users must exist in your Entra ID tenant before they can be provisioned<br />
                  • You must define App Roles in the application registration before assigning them<br />
                  • Removing a user's assignment doesn't remove their account, only their access<br />
                  • User assignments can also be managed through the Azure Portal
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
      
      {/* The user picker modal */}
      <UserPicker
        open={userPickerOpen}
        onClose={() => setUserPickerOpen(false)}
        onSubmit={handleUserPickerSubmit}
        graphAccessToken={graphTokenForUserSearch}
        multiSelect={false}
      />

      {/* The "View Sample JSON" help dialog */}
      <Dialog open={helpDialogOpen} onClose={handleCloseImportHelp} maxWidth="md">
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <UploadFileIcon sx={{ mr: 1 }} />
            Import File Format
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" paragraph>
            {appRoles.length > 0
              ? <>The imported file must be a JSON array of objects with a UPN and a roleId that matches one of this app’s roles.</>
              : <>The imported file must be a JSON array of objects with a UPN. Since this app has no roles, users will be assigned Default Access.</>}
          </Typography>
          <Box sx={{ backgroundColor: "#f7f7f7", borderRadius: 1, p: 2, fontFamily: "monospace", whiteSpace: "pre-wrap", mb: 2, border: "1px solid #e0e0e0", fontSize: "0.9rem" }}>
            {appRoles.length > 0
              ? `[
            { "upn": "alice@contoso.com", "roleId": "${appRoles[0]?.id || "00000000-0000-0000-0000-000000000000"}" },
            { "upn": "bob@fabrikam.com",  "roleId": "${appRoles[0]?.id || "00000000-0000-0000-0000-000000000000"}" }
          ]`
              : `[
            { "upn": "alice@contoso.com" },
            { "upn": "bob@fabrikam.com" }
          ]`}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadSampleJson} startIcon={<DownloadIcon />}>
            Download Sample JSON
          </Button>
          <Button onClick={handleCloseImportHelp} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editRoleDialogOpen} 
        onClose={() => setEditRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Update Role Assignment
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              User:
            </Typography>
            <Typography variant="body1">
              {editingUser?.displayName || editingUser?.userPrincipalName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editingUser?.userPrincipalName}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Role:
            </Typography>
            <Chip 
              label={editingUser?.assignedRoleValue || "None"} 
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <FormControl fullWidth>
            <InputLabel>New Role</InputLabel>
            <Select
              value={editRoleId}
              label="New Role"
              onChange={(e) => setEditRoleId(e.target.value)}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {appRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.displayName} ({role.value})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditRoleDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateUserRole}
            variant="contained"
            color="primary"
            disabled={!editRoleId || (editingUser?.assignedRoleValue === appRoles.find(role => role.id === editRoleId)?.value)}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default UserProvisioning;
